import { Icon } from '@iconify/react';
import { useMutation, useQuery } from '@tanstack/react-query';
/**
 * "I'm here" and "I'm done".
 *
 * The timesheet has always been something the owner typed in afterwards, from
 * memory — the least reliable source in the house and the one both sides end up
 * arguing with. One tap when she arrives is the only record either of them can
 * point at later.
 */
import { useEffect, useState } from 'react';
import { ApiError, api, isQueuedOffline } from '../api';
import { toastError, toastOk, toastQueued } from '../components/Toasts';
import { Skeleton } from '../components/ui/Skeleton';
import { t } from '../i18n';
import { onQueueChange, pending } from '../offline';
import { get, invalidate } from '../query';

const CLOCK_PATH = /\/v1\/shifts\/clock-(in|out)$/;

/** Is there a clock-in/out tap still sitting in the offline outbox? */
function useQueuedClockWrite(): boolean {
  const [queued, setQueued] = useState(false);
  useEffect(() => {
    let live = true;
    const check = () => {
      void pending().then((writes) => {
        if (live) setQueued(writes.some((w) => CLOCK_PATH.test(w.path)));
      });
    };
    check();
    const unsubscribe = onQueueChange(check);
    return () => {
      live = false;
      unsubscribe();
    };
  }, []);
  return queued;
}

interface Shift {
  id: string;
  started_at: string;
  ended_at: string | null;
  minutes: number | null;
}

function elapsed(startedAt: string): string {
  const mins = Math.max(0, Math.round((Date.now() - Date.parse(startedAt)) / 60_000));
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return h > 0 ? `${h}h ${m}m` : `${m}m`;
}

export function ClockCard() {
  const q = useQuery({
    queryKey: ['/v1/shifts/current'],
    queryFn: get<{ shift: Shift | null }>,
    // A running shift's elapsed time is the whole point of the card.
    refetchInterval: 60_000,
  });
  const shift = q.data?.shift ?? null;
  const queued = useQueuedClockWrite();

  const clock = useMutation({
    mutationFn: (dir: 'in' | 'out') =>
      api(`/v1/shifts/clock-${dir}`, { method: 'POST', body: '{}' }),
    onSuccess: (_r, dir) => toastOk(dir === 'in' ? t('Clocked in') : t('Clocked out')),
    onError: (e) => (isQueuedOffline(e) ? toastQueued(e.message) : toastError(e)),
    onSettled: () => invalidate('/v1/shifts', '/v1/payroll/work-entries'),
  });

  // Same shape as the card below, so the page doesn't jump when it lands.
  if (q.isPending) return <Skeleton className="h-[5.25rem] w-full rounded-[24px]" />;

  // A failed fetch must not look like "not clocked in" — the plain clock-in
  // button would invite a tap against a shift the server may already have
  // open. Same pattern as the retry card on StaffToday.
  if (q.isError) {
    return (
      <div className="flex w-full items-center justify-between gap-4 rounded-[24px] border border-urgent/25 bg-urgent/10 p-5">
        <span className="flex min-w-0 items-center gap-4">
          <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-[14px] bg-urgent/10 text-urgent">
            <Icon icon="ph:wifi-slash-bold" className="text-2xl" />
          </span>
          <span className="min-w-0 truncate text-sm font-bold text-urgent">
            {q.error instanceof ApiError ? q.error.message : t('Loading failed')}
          </span>
        </span>
        <button
          type="button"
          onClick={() => void q.refetch()}
          className="shrink-0 rounded-2xl bg-urgent/10 px-5 py-3.5 text-xs font-bold uppercase tracking-widest text-urgent transition-colors active:bg-urgent/20"
        >
          {t('Tap to retry')}
        </button>
      </div>
    );
  }

  return (
    <button
      type="button"
      // A queued tap has already been recorded — nothing here re-enables
      // until it either sends (the query refetches and `shift` flips) or the
      // outbox reports it landed. Without this, a person unsure the first
      // tap registered taps again, queuing a second clock-in behind it.
      disabled={clock.isPending || queued}
      onClick={() => clock.mutate(shift ? 'out' : 'in')}
      className={`flex w-full items-center justify-between gap-4 rounded-[24px] p-5 text-start transition-transform active:scale-[0.99] disabled:opacity-50 ${
        shift
          ? 'bg-[#111110] text-white shadow-[0_20px_40px_rgba(0,0,0,0.15)]'
          : 'border border-border bg-card shadow-[0_8px_30px_rgba(0,0,0,0.02)]'
      }`}
    >
      <span className="flex min-w-0 items-center gap-4">
        <span
          className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-[14px] ${
            shift ? 'bg-white/10 text-primary' : 'bg-primary/5 text-primary'
          }`}
        >
          <Icon
            icon={
              queued
                ? 'ph:cloud-arrow-up-bold'
                : shift
                  ? 'ph:stop-circle-bold'
                  : 'ph:play-circle-bold'
            }
            className="text-2xl"
          />
        </span>
        <span className="min-w-0">
          <span className="block truncate text-sm font-bold">
            {queued ? t('Waiting to send') : shift ? t("I'm done") : t("I'm here")}
          </span>
          <span
            className={`mt-0.5 block truncate text-[10px] font-bold uppercase tracking-widest ${
              shift ? 'text-white/70' : 'text-muted-foreground'
            }`}
          >
            {queued
              ? t('Will send when you have signal')
              : shift
                ? t('Working for {{time}}').replace('{{time}}', elapsed(shift.started_at))
                : t('Tap when you arrive')}
          </span>
        </span>
      </span>
    </button>
  );
}
